import { ACCOUNT_ENDPOINTS, AUTH_ENDPOINTS } from '@/constants/api';
import type { AuthTokens, CurrentUser, LoginResult, OAuthProvider } from '@/types/auth.types';

import { httpClient } from './httpClient';

/**
 * REST calls for authentication and account self-service — the sanctioned REST surface
 * (ADR-0002). Mirrors `Willnq.Api/Controllers/Identity/AuthController.cs` and
 * `AccountController.cs` request/response shapes exactly; do not add a field here that the
 * backend controller doesn't also declare.
 */
export const authService = {
  /**
   * `termsAccepted` is required by the endpoint, not optional metadata: the backend records one
   * immutable acceptance row per active legal document in the same transaction as the account, and
   * refuses the registration without it (ADR-0026).
   */
  async register(input: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    termsAccepted: boolean;
  }): Promise<void> {
    await httpClient.post(AUTH_ENDPOINTS.register, input);
  },

  async verifyEmail(input: { email: string; otp: string }): Promise<void> {
    await httpClient.post(AUTH_ENDPOINTS.verifyEmail, input);
  },

  /** Re-issues the verification OTP. Always succeeds (anti-enumeration) — never reveals whether
   * the email exists or is already verified. */
  async resendVerification(email: string): Promise<void> {
    await httpClient.post(AUTH_ENDPOINTS.resendVerification, { email });
  },

  /** MFA-enrolled accounts: call again with `mfaCode` once `mfaRequired` comes back true. */
  async login(input: { email: string; password: string; mfaCode?: string }): Promise<LoginResult> {
    const { data } = await httpClient.post<LoginResult>(AUTH_ENDPOINTS.login, input);
    return data;
  },

  /** MFA-required accounts: call again with `mfaCode` once `mfaRequired` comes back true (BR-2). */
  /**
   * `termsAccepted` is only consulted by the backend when this call would CREATE an account — an
   * existing account signing in again already accepted at registration and is never asked twice
   * (ADR-0026). Omitted (not merely `false`) on every ordinary sign-in call, so a first-time
   * registration that forgets to pass it is refused rather than silently treated as consent.
   */
  async loginWithGoogle(
    accessToken: string,
    mfaCode?: string,
    termsAccepted?: boolean,
  ): Promise<LoginResult> {
    const { data } = await httpClient.post<LoginResult>(AUTH_ENDPOINTS.google, {
      accessToken,
      mfaCode,
      termsAccepted,
    });
    return data;
  },

  /** Rotates the session via the `HttpOnly` refresh cookie — no token is sent or received in JS
   * (ADR-0006). Returns a fresh access token (+ current user) and rotates the cookie server-side. */
  async refresh(): Promise<AuthTokens> {
    const { data } = await httpClient.post<AuthTokens>(AUTH_ENDPOINTS.refresh, {});
    return data;
  },

  /** Revokes the refresh token (read from the cookie) and clears the cookie server-side. */
  async logout(): Promise<void> {
    await httpClient.post(AUTH_ENDPOINTS.logout, {});
  },

  async getCurrentUser(): Promise<CurrentUser> {
    const { data } = await httpClient.get<CurrentUser>(ACCOUNT_ENDPOINTS.me);
    return data;
  },

  /**
   * Set the caller's own WillnQ password, with exactly one credential re-proving the account holder.
   * An account that has a password sends `currentPassword`; one created through a social provider
   * (`CurrentUser.hasPassword === false`) sends a fresh provider token instead. Which credential is
   * accepted is decided server-side from the account, so a mixed account must send its password and a
   * token will not substitute.
   */
  async changePassword(input: {
    newPassword: string;
    currentPassword?: string;
    oAuthProvider?: OAuthProvider;
    oAuthAccessToken?: string;
  }): Promise<void> {
    await httpClient.post(ACCOUNT_ENDPOINTS.changePassword, input);
  },

  /**
   * Set or clear the caller's own phone number. Send the ten digits alone — the `+91` is a display
   * adornment on `PhoneField`, not part of the value. Pass null (or an empty string) to clear it.
   */
  async updatePhone(phone: string | null): Promise<void> {
    await httpClient.put(ACCOUNT_ENDPOINTS.phone, { phone: phone?.trim() || null });
  },

  /** Stage 1 of MFA enrollment: create/return a PENDING enrollment (secret + otpauth URI). Does not
   * enable MFA. Reuses an existing valid pending secret server-side if the user reopens the dialog. */
  async setupMfa(): Promise<{ secret: string; provisioningUri: string }> {
    const { data } = await httpClient.post<{ secret: string; provisioningUri: string }>(
      ACCOUNT_ENDPOINTS.mfaSetup,
    );
    return data;
  },

  /** Stage 2: verify the first TOTP code; MFA activates only on success. */
  async verifyMfa(code: string): Promise<void> {
    await httpClient.post(ACCOUNT_ENDPOINTS.mfaVerify, { code });
  },

  /** Discard an in-progress enrollment; MFA stays disabled. */
  async cancelMfa(): Promise<void> {
    await httpClient.post(ACCOUNT_ENDPOINTS.mfaCancel);
  },

  /**
   * Lost-authenticator reset, stage 1: re-authenticate; the server emails a one-time code.
   *
   * Takes the account's password OR a freshly-obtained provider access token — <b>exactly one</b>, which
   * the union type enforces here rather than leaving to a runtime check. The backend refuses both-or-neither
   * (it will not pick one for you), and which applies is decided by the account: `hasPassword` and
   * `linkedOAuthProviders` on `CurrentUser`.
   */
  async requestMfaReset(
    proof:
      | { password: string }
      | { oAuthProvider: OAuthProvider; oAuthAccessToken: string },
  ): Promise<void> {
    await httpClient.post(ACCOUNT_ENDPOINTS.mfaResetRequest, proof);
  },

  /**
   * Stage 2: spend the emailed code. MFA comes off and every session is revoked, so the caller must
   * re-enroll immediately (`turnOffCompletely: false`) unless the account is allowed to go without.
   */
  async confirmMfaReset(input: { otp: string; turnOffCompletely: boolean }): Promise<void> {
    await httpClient.post(ACCOUNT_ENDPOINTS.mfaResetConfirm, input);
  },

  async forgotPassword(email: string): Promise<void> {
    await httpClient.post(ACCOUNT_ENDPOINTS.forgotPassword, { email });
  },

  async resetPassword(input: { email: string; token: string; newPassword: string }): Promise<void> {
    await httpClient.post(ACCOUNT_ENDPOINTS.resetPassword, input);
  },

  /**
   * Pre-authentication lost-authenticator reset, stage 1: confirm the account password; the server
   * emails a one-time code. Always resolves (anti-enumeration) — never reveals whether the account
   * exists, has MFA enabled, or the password was right.
   */
  async forgotMfa(email: string, password: string): Promise<void> {
    await httpClient.post(ACCOUNT_ENDPOINTS.forgotMfa, { email, password });
  },

  /**
   * Stage 2: spend the emailed code. MFA comes off and every session is revoked. Always requests the
   * "new device" outcome (`turnOffCompletely: false`) — a caller with no working session has no way
   * to know their own role, so the server-enforced BR-2 guard is what actually decides whether a full
   * disable is even possible; this pre-auth flow never offers "turn off MFA for good" as a choice
   * (see `ResetMfaDialog` for the authenticated flow, which does).
   */
  async resetMfa(input: { email: string; otp: string }): Promise<void> {
    await httpClient.post(ACCOUNT_ENDPOINTS.resetMfa, { ...input, turnOffCompletely: false });
  },

  /**
   * Provider-based pre-auth recovery, step 1: hand the server a freshly-obtained provider token and ask
   * whether it can recover an account. **Resets nothing** — that is what makes it safe to call the moment
   * the provider popup closes, before the user has agreed to anything.
   *
   * Sends no email address: the account is resolved from the token's provider subject server-side, so
   * whatever was typed on the first screen cannot influence which account this is about.
   */
  async checkOAuthMfaRecovery(input: {
    provider: OAuthProvider;
    accessToken: string;
  }): Promise<{ eligible: boolean; accountEmail: string | null }> {
    const response = await httpClient.post<{ eligible: boolean; accountEmail: string | null }>(
      ACCOUNT_ENDPOINTS.oauthMfaRecoveryCheck,
      input,
    );
    return response.data;
  },

  /**
   * Step 2: remove the authenticator and revoke every session. Called only after the user confirms on a
   * screen that says what it destroys — the split into two requests is what keeps a successful provider
   * sign-in from being a reset by itself. The server emails a security notification afterwards.
   */
  async resetMfaViaOAuth(input: { provider: OAuthProvider; accessToken: string }): Promise<void> {
    await httpClient.post(ACCOUNT_ENDPOINTS.oauthMfaRecoveryReset, input);
  },
};
